import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * SEO Component - Manages meta tags and document title for each page
 * 
 * Usage:
 * <SEO 
 *   title="Page Title"
 *   description="Page description"
 *   keywords="keyword1, keyword2"
 *   ogImage="/path/to/image.png"
 * />
 */
const SEO = ({
  title = "Loops & Purls",
  description = "Loops & Purls — Create and share knitting patterns with our free chart maker. Design stitch charts, export PDFs, and browse pattern gallery — no account required.",
  keywords = "knitting patterns, knitting chart maker, stitch chart, pattern creator, knitting design, purl stitch, knit stitch, knitting app, knitting charts",
  ogImage = "/square-logo.png",
  ogType = "website",
}) => {
  const location = useLocation();
  
  // Get base URL from environment or use production domain
  const baseUrl = process.env.REACT_APP_BASE_URL || "https://loopsandpurls.com";
  const canonicalUrl = `${baseUrl}${location.pathname}`;

  useEffect(() => {
    // Update document title
    document.title = title === "Loops & Purls" 
      ? "Loops & Purls — Knitting Pattern Chart Maker"
      : `${title} | Loops & Purls`;

    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.setAttribute("name", "description");
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute("content", description);

    // Update or create keywords meta tag
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement("meta");
      metaKeywords.setAttribute("name", "keywords");
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute("content", keywords);

    // Update or create canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", canonicalUrl);

    // Update Open Graph tags
    const updateOGTag = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    updateOGTag("og:title", title);
    updateOGTag("og:description", description);
    updateOGTag("og:type", ogType);
    updateOGTag("og:url", canonicalUrl);
    updateOGTag("og:image", baseUrl ? `${baseUrl}${ogImage}` : ogImage);

    // Update Twitter Card tags
    const updateTwitterTag = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    updateTwitterTag("twitter:card", "summary_large_image");
    updateTwitterTag("twitter:title", title);
    updateTwitterTag("twitter:description", description);
    updateTwitterTag("twitter:image", baseUrl ? `${baseUrl}${ogImage}` : ogImage);

  }, [title, description, keywords, ogImage, ogType, canonicalUrl, baseUrl]);

  return null; // This component doesn't render anything
};

export default SEO;

